import React from 'react';
import { useFeature, useAnyFeature, useAllFeatures } from './FeatureContext';
import { FeatureMessage } from './FeatureMessage';

interface FeatureSwitchGateProps {
  /** Single feature check */
  feature?: string;
  /** OR logic - at least one feature required */
  anyOf?: string[];
  /** AND logic - all features required */
  allOf?: string[];
  /** Content to render if feature is enabled */
  children: React.ReactNode;
  /** Optional fallback content if feature is disabled */
  fallback?: React.ReactNode;
  /** Show disabled message when feature is off (default: false) */
  showDisabledMessage?: boolean;
}

export const FeatureSwitchGate: React.FC<FeatureSwitchGateProps> = ({
  feature,
  anyOf,
  allOf,
  children,
  fallback,
  showDisabledMessage = false,
}) => {
  const singleFeature = useFeature(feature || '');
  const anyFeature = useAnyFeature(anyOf || []);
  const allFeatures = useAllFeatures(allOf || []);

  // Priority: single > anyOf > allOf > default true
  const isEnabled = feature
    ? singleFeature
    : anyOf
      ? anyFeature
      : allOf
        ? allFeatures
        : true;

  if (isEnabled) return <>{children}</>;
  if (fallback !== undefined) return <>{fallback}</>;
  if (!showDisabledMessage) return null;

  const requiredFeatures = feature
    ? [feature]
    : anyOf
      ? anyOf
      : allOf
        ? allOf
        : [];
  const requirementType: 'single' | 'any' | 'all' = feature
    ? 'single'
    : anyOf
      ? 'any'
      : 'all';

  return (
    <FeatureMessage
      requiredFeatures={requiredFeatures}
      requirementType={requirementType}
    />
  );
};
